/**
 * GPX Parser Utility
 * Parses GPX XML files and extracts waypoints from <wpt> and <rtept> elements
 */

export interface ParsedWaypoint {
  lat: number
  lon: number
  name?: string
  notes?: string
  elevation?: number
  metadata?: Record<string, any>
}

export interface ParsedGpxData {
  waypoints: ParsedWaypoint[]
  routePoints: ParsedWaypoint[] // Route points found but not imported
  routeName?: string
  routeDescription?: string
  metadata?: Record<string, any>
}

/**
 * Parse OnX description format: name=Blue lake\nnotes=\nid=...\ntype=Line\n...
 */
function parseOnXDescription(desc: string): Record<string, string> {
  const result: Record<string, string> = {}
  const lines = desc.split('\n')
  
  for (const line of lines) {
    const match = line.match(/^([^=]+)=(.*)$/)
    if (match) {
      const key = match[1].trim()
      const value = match[2].trim()
      result[key] = value
    }
  }
  
  return result
}

/**
 * Extract OnX extensions from XML element
 */
function extractOnXExtensions(element: Element): Record<string, any> {
  const extensions: Record<string, any> = {}
  
  // Look for onx namespace elements
  const onxElements = element.getElementsByTagNameNS('https://wwww.onxmaps.com/', '*')
  for (let i = 0; i < onxElements.length; i++) {
    const el = onxElements[i]
    const tagName = el.tagName.replace('onx:', '')
    extensions[tagName] = el.textContent || ''
  }
  
  return extensions
}

/**
 * Parse GPX XML string and extract waypoints
 */
export function parseGpx(gpxXml: string): ParsedGpxData {
  const parser = new DOMParser()
  const doc = parser.parseFromString(gpxXml, 'text/xml')
  
  // Check for parsing errors
  const parserError = doc.querySelector('parsererror')
  if (parserError) {
    throw new Error(`Failed to parse GPX XML: ${parserError.textContent}`)
  }
  
  const waypoints: ParsedWaypoint[] = []
  const routePoints: ParsedWaypoint[] = [] // Route points found but not imported
  let routeName: string | undefined
  let routeDescription: string | undefined
  const metadata: Record<string, any> = {}
  
  // Extract standard waypoints (<wpt>) - these are the ones we want
  const wptElements = doc.getElementsByTagName('wpt')
  for (let i = 0; i < wptElements.length; i++) {
    const wpt = wptElements[i]
    const lat = parseFloat(wpt.getAttribute('lat') || '')
    const lon = parseFloat(wpt.getAttribute('lon') || '')
    
    if (isNaN(lat) || isNaN(lon)) {
      continue
    }
    
    const nameEl = wpt.querySelector('name')
    const descEl = wpt.querySelector('desc')
    const eleEl = wpt.querySelector('ele')
    
    const waypoint: ParsedWaypoint = {
      lat,
      lon,
      name: nameEl?.textContent || undefined,
      notes: descEl?.textContent || undefined,
      elevation: eleEl ? parseFloat(eleEl.textContent || '') : undefined,
      metadata: {}
    }
    
    // Extract OnX extensions if present
    const onxExtensions = extractOnXExtensions(wpt)
    if (Object.keys(onxExtensions).length > 0) {
      waypoint.metadata = { ...waypoint.metadata, onx: onxExtensions }
    }
    
    waypoints.push(waypoint)
  }
  
  // Extract route points (<rtept>) from routes (<rte>) - these we DON'T want to import
  const rteElements = doc.getElementsByTagName('rte')
  for (let i = 0; i < rteElements.length; i++) {
    const rte = rteElements[i]
    
    // Extract route name and description (for metadata only)
    const rteNameEl = rte.querySelector('name')
    const rteDescEl = rte.querySelector('desc')
    
    if (rteNameEl && !routeName) {
      routeName = rteNameEl.textContent || undefined
    }
    
    if (rteDescEl && !routeDescription) {
      routeDescription = rteDescEl.textContent || undefined
      
      // Parse OnX description format
      const onxDesc = parseOnXDescription(routeDescription || '')
      if (Object.keys(onxDesc).length > 0) {
        metadata.onxDescription = onxDesc
      }
    }
    
    // Extract OnX extensions from route
    const rteOnxExtensions = extractOnXExtensions(rte)
    if (Object.keys(rteOnxExtensions).length > 0) {
      metadata.onxRouteExtensions = rteOnxExtensions
    }
    
    // Extract route points - but DON'T add them to waypoints, add to routePoints instead
    const rteptElements = rte.getElementsByTagName('rtept')
    for (let j = 0; j < rteptElements.length; j++) {
      const rtept = rteptElements[j]
      const lat = parseFloat(rtept.getAttribute('lat') || '')
      const lon = parseFloat(rtept.getAttribute('lon') || '')
      
      if (isNaN(lat) || isNaN(lon)) {
        continue
      }
      
      const nameEl = rtept.querySelector('name')
      const descEl = rtept.querySelector('desc')
      const eleEl = rtept.querySelector('ele')
      
      const routePoint: ParsedWaypoint = {
        lat,
        lon,
        name: nameEl?.textContent || routeName || `Route Point ${routePoints.length + 1}`,
        notes: descEl?.textContent || undefined,
        elevation: eleEl ? parseFloat(eleEl.textContent || '') : undefined,
        metadata: {
          source: 'route',
          routeName: routeName,
          ...(Object.keys(rteOnxExtensions).length > 0 ? { onx: rteOnxExtensions } : {})
        }
      }
      
      routePoints.push(routePoint)
    }
  }
  
  return {
    waypoints,
    routePoints,
    routeName,
    routeDescription,
    metadata
  }
}

/**
 * Read GPX file and parse it
 */
export async function parseGpxFile(file: File): Promise<ParsedGpxData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const parsed = parseGpx(text)
        resolve(parsed)
      } catch (error) {
        reject(error)
      }
    }
    
    reader.onerror = () => {
      reject(new Error('Failed to read GPX file'))
    }
    
    reader.readAsText(file)
  })
}

